import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { Spinner, StepIndicator } from "@/components/ds";
import { fadeSlideUp, springGentle } from "@/components/ds/motion";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { OnboardingCompanyStep } from "@/components/app/onboarding/OnboardingCompanyStep";
import { OnboardingSiteStep } from "@/components/app/onboarding/OnboardingSiteStep";
import { OnboardingInviteStep } from "@/components/app/onboarding/OnboardingInviteStep";
import { OnboardingComplete } from "@/components/app/onboarding/OnboardingComplete";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./OnboardingPage.module.css";

const STEPS = ["Company", "First site", "First cleaner"] as const;

interface AuthInfo {
  readonly userName: string;
  readonly orgName: string;
}

export function OnboardingPage() {
  usePageTitle("Get started | TidyRota");

  const navigate = useNavigate();
  const [auth, setAuth] = useState<AuthInfo | null>(null);
  const [checking, setChecking] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const completingRef = useRef(false);

  useEffect(() => {
    fetch("/api/auth/me")
      .then(async (res) => {
        if (!res.ok) {
          navigate("/login", { replace: true });
          return;
        }
        const data = await res.json();
        const membership = data.data?.membership;
        if (membership?.onboardingCompleted) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setAuth({
          userName: data.data.user.name,
          orgName: data.data.org.name,
        });
        setChecking(false);
      })
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, [navigate]);

  const completeOnboarding = useCallback(async () => {
    if (completingRef.current) return;
    completingRef.current = true;
    await fetch("/api/onboarding/complete", { method: "POST" });
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  function handleStepComplete() {
    if (currentStep < 2) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setCurrentStep(3);
    }
  }

  function handleStepSkip() {
    handleStepComplete();
  }

  if (checking) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spinner />
      </div>
    );
  }

  if (!auth) return null;

  return (
    <AuthLayout>
      {currentStep < 3 ? (
        <>
          <StepIndicator steps={STEPS} currentStep={currentStep} />
          <div className={styles.stepWrapper}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                variants={fadeSlideUp}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springGentle}
              >
                {currentStep === 0 && (
                  <OnboardingCompanyStep
                    userName={auth.userName}
                    orgName={auth.orgName}
                    onComplete={handleStepComplete}
                    onSkip={handleStepSkip}
                  />
                )}
                {currentStep === 1 && (
                  <OnboardingSiteStep
                    onComplete={handleStepComplete}
                    onSkip={handleStepSkip}
                  />
                )}
                {currentStep === 2 && (
                  <OnboardingInviteStep
                    onComplete={handleStepComplete}
                    onSkip={handleStepSkip}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className={styles.skipSetup}>
            <button type="button" className={styles.skipSetupButton} onClick={completeOnboarding}>
              Skip setup
            </button>
          </div>
        </>
      ) : (
        <OnboardingComplete onDone={completeOnboarding} />
      )}
    </AuthLayout>
  );
}
